import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { majorToMinor, minorToMajor } from "@/core/utils/func/money-minor";
import { useUpdateSavingsVault } from "@/features/savings/api/use-update-savings-vault";
import type {
  SavingsVaultDto,
  UpdateSavingsVaultRequestDto,
} from "@/features/savings/api/dto";
import {
  createUpdateSavingsVaultSchema,
  type UpdateSavingsVaultFormValues,
} from "@/features/savings/schemas/savings-vault.schema";

/**
 * Renommage / objectif d'un coffre (`PUT /SavingsVaults/{id}`, idempotent)
 * — titulaire uniquement, coffre OUVERT. `goalAmount` 0 / vide retire
 * l'objectif (`goalAmountMinor: null`). Opération sans frais :
 * `MutationConfirmPreview` avant l'appel API.
 */
export function VaultRenameDialog({
  vault,
  open,
  onOpenChange,
}: {
  vault: SavingsVaultDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  const tSavings = (key: string) => t(key as TFunctionType);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit reconstruit au changement de langue.
  const schema = useMemo(() => createUpdateSavingsVaultSchema(), [i18n.language]);

  const update = useUpdateSavingsVault(vault.id);
  const pending = update.isPending;
  const [preview, setPreview] = useState<UpdateSavingsVaultRequestDto | null>(
    null
  );

  const form = useForm({
    defaultValues: {
      name: vault.name,
      goalAmount: vault.goalAmountMinor
        ? minorToMajor(vault.goalAmountMinor, vault.currency)
        : undefined,
    } as UpdateSavingsVaultFormValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview({
        name: parsed.data.name,
        goalAmountMinor:
          parsed.data.goalAmount && parsed.data.goalAmount > 0
            ? majorToMinor(parsed.data.goalAmount, vault.currency)
            : null,
      });
    },
  });

  const fieldError =
    (name: keyof UpdateSavingsVaultFormValues) =>
    ({ value }: { value: unknown }) => {
      const result = schema.shape[name].safeParse(value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t("savings.vaults.rename.title")}</DialogTitle>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field name="name" validators={{ onChange: fieldError("name") }}>
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="savings-rename-name">
                    {t("savings.vaults.form.name")}
                  </Label>
                  <Input
                    id="savings-rename-name"
                    maxLength={80}
                    placeholder={t("savings.vaults.form.namePlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="goalAmount"
              validators={{ onChange: fieldError("goalAmount") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="savings-rename-goal">
                    {t("savings.vaults.form.goalAmount")}
                  </Label>
                  <Input
                    id="savings-rename-goal"
                    type="number"
                    min={0}
                    className="notranslate"
                    placeholder={t("savings.vaults.form.goalAmountPlaceholder")}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(
                        Number.isNaN(e.target.valueAsNumber)
                          ? undefined
                          : e.target.valueAsNumber
                      )
                    }
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreview(null);
        }}
        data={preview ? { vaultId: vault.id, ...preview } : {}}
        title={t("common.mutationPreview.updateTitle")}
        description={t("common.mutationPreview.updateDescription")}
        t={tSavings}
        pending={pending}
        onConfirm={() => {
          if (!preview) return;
          update.mutate(preview, {
            onSuccess: () => {
              notifSilence(t("savings.vaults.feedback.updated"));
              setPreview(null);
              onOpenChange(false);
            },
            onError: (error) =>
              notifSilenceError(apiErrorHandler(error) || error.message),
          });
        }}
      />
    </>
  );
}
